"use client";

import { Button, Modal } from "@mantine/core";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useId, useMemo, useState } from "react";

import { Icon, type IconName } from "../../icons";
import type { TourBodyBlock, TourModalProps, TourStep } from "../../types";
import { cn } from "../../utils";

const DEFAULT_NAMESPACE = "tour";

const SEARCH_CATEGORY_TINT: Record<string, string> = {
  amber: "bg-amber-100 text-amber-600",
  blue: "bg-blue-100 text-blue-600",
  green: "bg-green-100 text-green-600",
  primary: "bg-primary/10 text-primary",
};

const TOGGLE_SECTION_TINT: Record<string, { header: string; icon: string }> = {
  amber: { header: "bg-amber-50", icon: "text-amber-600" },
  blue: { header: "bg-blue-50", icon: "text-blue-600" },
  green: { header: "bg-green-50", icon: "text-green-600" },
  primary: { header: "bg-primary/10", icon: "text-primary" },
};

export function TourModal({
  opened,
  onClose,
  steps,
  namespace = DEFAULT_NAMESPACE,
  primaryButtonColor = "red",
}: TourModalProps) {
  const t = useTranslations(namespace);
  const titleId = useId();
  const [index, setIndex] = useState(0);

  // Back to the first step on every open, during render so the last step
  // never paints first.
  const [prev, setPrev] = useState({ opened, steps });
  if (opened !== prev.opened || steps !== prev.steps) {
    setPrev({ opened, steps });
    if (opened) setIndex(0);
  }

  const labels = useMemo(
    () => ({
      skip: t("buttons.skip"),
      back: t("buttons.back"),
      next: t("buttons.next"),
      finish: t("buttons.finish"),
      getStarted: t("buttons.getStarted"),
      stepsLabel: t("a11y.steps-label"),
      stepNumbers: Array.from({ length: steps.length }, (_, i) =>
        t("a11y.step-number", { n: i + 1, total: steps.length }),
      ),
    }),
    [t, steps.length],
  );

  if (steps.length === 0) return null;

  const safeIndex = Math.min(index, steps.length - 1);
  const step = steps[safeIndex]!;
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const handleBack = () => setIndex((i) => Math.max(i - 1, 0));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size="lg"
      padding={0}
      radius="lg"
      zIndex={1000}
      aria-labelledby={titleId}
      classNames={{
        overlay: "backdrop-blur-xs bg-black/50",
        content: "overflow-hidden max-h-[90dvh]! rounded-2xl!",
        body: "p-0",
        inner: "p-4! sm:p-8!",
      }}
      fullScreen={false}
    >
      <div
        data-testid="tour-modal"
        className="flex max-h-[calc(90dvh)] flex-col bg-white"
      >
        <TourBanner
          titleId={titleId}
          title={t(step.bannerKey)}
          onClose={onClose}
          aria-label={t("a11y.close-tour")}
        />

        <div className="shell-scrollbar flex-1 overflow-y-auto px-5 py-4 sm:px-8 sm:py-7">
          <TourStepContent step={step} t={t} />
        </div>

        <TourFooter
          isFirst={isFirst}
          isLast={isLast}
          stepsCount={steps.length}
          activeIndex={safeIndex}
          onSkip={onClose}
          onBack={handleBack}
          onNext={handleNext}
          onJump={setIndex}
          labels={labels}
          primaryButtonColor={primaryButtonColor}
        />
      </div>
    </Modal>
  );
}

function TourBanner({
  titleId,
  title,
  onClose,
  "aria-label": ariaLabel,
}: {
  titleId: string;
  title: string;
  onClose: () => void;
  "aria-label"?: string;
}) {
  return (
    <div className="flex items-center justify-between bg-primary px-6 py-4 text-white">
      <span id={titleId} className="text-base font-semibold">
        {title}
      </span>
      <button
        type="button"
        onClick={onClose}
        data-testid="tour-modal-close"
        className="flex size-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/15 hover:text-white"
        aria-label={ariaLabel}
      >
        <Icon name="close" size={20} />
      </button>
    </div>
  );
}

type TFn = ReturnType<typeof useTranslations>;

function TourStepContent({ step, t }: { step: TourStep; t: TFn }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary sm:mb-4 sm:size-16">
        <Icon name={step.iconName} size={24} />
      </div>
      <h2 className="text-xl font-semibold text-text-primary sm:text-2xl">
        {t(step.headingKey)}
      </h2>
      <p className="mt-1.5 max-w-md text-sm text-text-secondary sm:mt-3 sm:text-base">
        {t(step.descriptionKey)}
      </p>

      {step.blocks?.length ? (
        <div className="mt-3 flex w-full flex-col gap-2.5 text-left sm:mt-6 sm:gap-4">
          {step.blocks.map((block, i) => (
            <TourBlockRenderer key={i} block={block} t={t} />
          ))}
        </div>
      ) : null}

      {step.footerNote ? (
        <div className="mt-5 flex w-full items-start gap-2 rounded-lg bg-primary/5 px-3 py-2 text-left text-xs text-text-secondary sm:text-sm">
          <Icon
            name={step.footerNote.iconName ?? "check"}
            size={16}
            className="mt-0.5 shrink-0 text-primary"
          />
          <span>{t(step.footerNote.textKey)}</span>
        </div>
      ) : null}
    </div>
  );
}

function TourBlockRenderer({ block, t }: { block: TourBodyBlock; t: TFn }) {
  switch (block.kind) {
    case "highlight":
      return (
        <div className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-4 text-center">
          {block.titleKey ? (
            <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
              {t(block.titleKey)}
            </p>
          ) : null}
          <p className="mt-1 text-3xl font-bold text-primary sm:text-4xl">
            {t(block.valueKey)}
          </p>
          {block.subtitleKey ? (
            <p className="mt-1 text-xs text-text-secondary sm:text-sm">
              {t(block.subtitleKey)}
            </p>
          ) : null}
        </div>
      );

    case "note":
      return (
        <div className="flex items-start gap-3 rounded-lg border border-surface-border bg-white px-3 py-2.5">
          <Icon
            name={block.iconName ?? "check"}
            size={18}
            className="mt-0.5 shrink-0 text-primary"
          />
          <div>
            <p className="text-sm font-medium text-text-primary">
              {t(block.titleKey)}
            </p>
            {block.subtitleKey ? (
              <p className="mt-0.5 text-xs text-text-secondary">
                {t(block.subtitleKey)}
              </p>
            ) : null}
          </div>
        </div>
      );

    case "usageOptions":
      return (
        <div>
          <p className="mb-2 text-sm font-medium text-text-primary">
            {t(block.titleKey)}
          </p>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {block.options.map((opt, i) => (
              <UsageOption
                key={i}
                t={t}
                option={opt}
                isLast={i === block.options.length - 1}
                conjunctionKey={block.conjunctionKey}
              />
            ))}
          </div>
        </div>
      );

    case "featureList": {
      const variant = block.variant ?? "card";
      if (variant === "circleRows") {
        return (
          <ul className="mx-auto flex w-fit flex-col gap-1 sm:gap-3">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:size-11">
                  <Icon name={item.iconName} size={18} />
                </div>
                <div className="min-w-0 pt-0.5">
                  <p className="text-sm font-semibold text-text-primary">
                    {t(item.labelKey)}
                  </p>
                  {item.subLabelKey ? (
                    <p className="mt-0.5 text-xs text-text-secondary sm:text-sm">
                      {t(item.subLabelKey)}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        );
      }
      if (variant === "grouped") {
        return (
          <div className="mx-auto w-fit rounded-2xl bg-gray-100 px-4 py-3 sm:px-5 sm:py-4">
            <ul className="flex flex-col gap-2.5 sm:gap-3.5">
              {block.items.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Icon
                    name={item.iconName}
                    size={20}
                    className="shrink-0 text-primary"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary">
                      {t(item.labelKey)}
                    </p>
                    {item.subLabelKey ? (
                      <p className="mt-0.5 text-xs text-text-secondary">
                        {t(item.subLabelKey)}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      const plain = variant === "plain";
      return (
        <div
          className={cn(
            "mx-auto grid w-fit gap-3",
            block.layout === "grid"
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1",
          )}
        >
          {block.items.map((item, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center gap-3",
                plain
                  ? "px-1"
                  : "items-start rounded-lg border border-surface-border bg-white px-3 py-3",
              )}
            >
              {plain ? (
                <Icon
                  name={item.iconName}
                  size={22}
                  className="shrink-0 text-primary"
                />
              ) : (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon name={item.iconName} size={18} />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary">
                  {t(item.labelKey)}
                </p>
                {item.subLabelKey ? (
                  <p className="mt-0.5 text-xs text-text-secondary">
                    {t(item.subLabelKey)}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      );
    }

    case "video":
      return (
        <div className="w-full">
          {block.titleKey ? (
            <p className="mb-2 text-sm font-medium text-text-primary">
              {t(block.titleKey)}
            </p>
          ) : null}
          <div className="relative w-full overflow-hidden rounded-xl border border-surface-border bg-black pt-[56.25%]">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${block.videoId}`}
              title={t("a11y.video-title")}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 size-full"
            />
          </div>
        </div>
      );

    case "illustration": {
      const decorative = !block.altKey;
      return (
        <div className="flex w-full justify-center">
          <Image
            src={block.src}
            alt={decorative ? "" : t(block.altKey!)}
            width={block.width ?? 600}
            height={block.height ?? 400}
            unoptimized
            aria-hidden={decorative ? true : undefined}
            className={cn("h-auto w-full", block.maxWidthClass ?? "max-w-sm")}
          />
        </div>
      );
    }

    case "toolGrid":
      return (
        <div className="grid w-full grid-cols-2 gap-3">
          {block.items.map((item, i) => {
            const isOddLast =
              block.items.length % 2 === 1 && i === block.items.length - 1;
            return (
              <div
                key={i}
                className={cn(
                  "flex flex-col items-center gap-2 rounded-xl bg-gray-50 px-4 py-6 text-center",
                  isOddLast && "col-span-2",
                )}
              >
                <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon name={item.iconName} size={24} />
                </div>
                <p className="mt-1 text-sm font-semibold text-text-primary">
                  {t(item.labelKey)}
                </p>
                {item.subLabelKey ? (
                  <p className="text-xs text-text-secondary">
                    {t(item.subLabelKey)}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      );

    case "chipsChat":
      return (
        <div className="w-full space-y-4 text-left">
          {block.chipsTitleKey ? (
            <p className="text-sm font-medium text-text-primary">
              {t(block.chipsTitleKey)}
            </p>
          ) : null}
          <div className="mx-auto flex max-w-[320px] flex-wrap gap-2">
            {block.chips.map((chip, i) => (
              <span
                key={i}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium",
                  chip.active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 bg-white text-gray-600",
                )}
              >
                {chip.iconName ? <Icon name={chip.iconName} size={14} /> : null}
                {t(chip.labelKey)}
              </span>
            ))}
          </div>
          <div className="space-y-3 rounded-xl bg-gray-100 p-3">
            <div className="mr-auto flex max-w-[90%] items-start gap-2">
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                <Icon name="chatBot" size={16} />
              </span>
              <div className="rounded-2xl rounded-tl-sm bg-white px-4 py-2.5 text-sm text-text-primary">
                {t(block.assistantBubbleKey)}
              </div>
            </div>
            <div className="ml-auto flex max-w-[85%] items-end justify-end gap-2">
              <div className="rounded-2xl rounded-br-sm bg-primary px-4 py-2 text-xs text-white">
                {t(block.userBubbleKey)}
              </div>
              {block.avatarSrc ? (
                <Image
                  src={block.avatarSrc}
                  alt=""
                  width={28}
                  height={28}
                  unoptimized
                  aria-hidden
                  className="size-7 shrink-0 rounded-full"
                />
              ) : (
                <span
                  aria-hidden
                  className="size-7 shrink-0 rounded-full bg-surface-border"
                />
              )}
            </div>
          </div>
        </div>
      );

    case "searchCategories": {
      return (
        <div className="w-full space-y-3">
          <div className="flex items-center gap-2 rounded-xl border border-surface-border bg-white px-4 py-3 text-text-secondary">
            <Icon name="search" size={18} />
            <span className="text-sm">{t(block.searchPlaceholderKey)}</span>
          </div>
          <div className="flex flex-col gap-3">
            {block.categories.map((cat, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-2xl border border-surface-border bg-white px-4 py-3.5 shadow-xs"
              >
                <div
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-xl",
                    SEARCH_CATEGORY_TINT[cat.tint ?? "primary"],
                  )}
                >
                  <Icon name={cat.iconName} size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-primary">
                    {t(cat.labelKey)}
                  </p>
                  {cat.subLabelKey ? (
                    <p className="mt-0.5 text-xs text-text-secondary">
                      {t(cat.subLabelKey)}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "toggleSection": {
      return (
        <div className="w-full space-y-3">
          {block.items.map((item, i) => {
            const tint = TOGGLE_SECTION_TINT[item.tint ?? "primary"]!;
            return (
              <div
                key={i}
                className="overflow-hidden rounded-xl border border-surface-border bg-white"
              >
                <div
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-sm font-semibold text-text-primary",
                    tint.header,
                  )}
                >
                  <Icon name={item.iconName} size={18} className={tint.icon} />
                  {t(item.titleKey)}
                </div>
                <div className="px-4 py-4">
                  <Image
                    src={item.illustrationSrc}
                    alt=""
                    width={400}
                    height={100}
                    unoptimized
                    aria-hidden
                    className="h-auto w-full"
                  />
                  {item.footerLabelKey ? (
                    <div className="mt-3 flex items-center gap-2 text-xs text-text-secondary">
                      <Icon
                        name={item.footerIconName ?? "check"}
                        size={14}
                        className="shrink-0 text-green-600"
                      />
                      <span>{t(item.footerLabelKey)}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    case "infoNote":
      return (
        <div className="mx-auto flex w-full max-w-md items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-left text-xs text-text-secondary sm:py-2.5 sm:text-sm">
          <Icon
            name={block.iconName ?? "info"}
            size={16}
            className="mt-0.5 shrink-0 text-text-secondary"
          />
          <span>{t(block.textKey)}</span>
        </div>
      );

    case "example":
      return (
        <div className="rounded-lg border border-surface-border bg-white px-4 py-3 text-sm">
          {block.titleKey ? (
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
              {t(block.titleKey)}
            </p>
          ) : null}
          <ul className="flex flex-col gap-1.5">
            {block.lines.map((line, i) => (
              <li
                key={i}
                className={cn(
                  "flex items-center justify-between",
                  line.emphasis === "total" &&
                    "border-t border-surface-border pt-1.5 font-semibold",
                )}
              >
                <span className="text-text-secondary">{t(line.labelKey)}</span>
                <span
                  className={cn(
                    line.emphasis === "negative"
                      ? "text-text-danger"
                      : line.emphasis === "total"
                        ? "text-primary"
                        : "text-text-primary",
                  )}
                >
                  {t(line.valueKey)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      );

    default:
      return null;
  }
}

function UsageOption({
  t,
  option,
  isLast,
  conjunctionKey,
}: {
  t: TFn;
  option: { iconName?: IconName; labelKey: string; valueKey?: string };
  isLast: boolean;
  conjunctionKey?: string;
}) {
  return (
    <>
      <div className="flex flex-1 items-start gap-2 rounded-lg border border-surface-border bg-white px-3 py-2.5">
        {option.iconName ? (
          <Icon
            name={option.iconName}
            size={18}
            className="mt-0.5 shrink-0 text-primary"
          />
        ) : null}
        <div className="min-w-0">
          {option.valueKey ? (
            <p className="text-sm font-semibold text-text-primary">
              {t(option.valueKey)}
            </p>
          ) : null}
          <p className="text-xs text-text-secondary">{t(option.labelKey)}</p>
        </div>
      </div>
      {!isLast && conjunctionKey ? (
        <span className="text-center text-xs font-semibold uppercase tracking-wide text-text-secondary">
          {t(conjunctionKey)}
        </span>
      ) : null}
    </>
  );
}

function TourFooter({
  isFirst,
  isLast,
  stepsCount,
  activeIndex,
  onSkip,
  onBack,
  onNext,
  onJump,
  labels,
  primaryButtonColor,
}: {
  isFirst: boolean;
  isLast: boolean;
  stepsCount: number;
  activeIndex: number;
  onSkip: () => void;
  onBack: () => void;
  onNext: () => void;
  onJump: (index: number) => void;
  labels: {
    skip: string;
    back: string;
    next: string;
    finish: string;
    getStarted: string;
    stepsLabel: string;
    stepNumbers: string[];
  };
  primaryButtonColor: string;
}) {
  return (
    <div className="flex flex-col gap-3 border-t border-surface-border bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
      <button
        type="button"
        onClick={onSkip}
        data-testid="tour-modal-skip"
        className="order-3 text-sm text-text-secondary transition hover:text-text-primary sm:order-1"
      >
        {labels.skip}
      </button>

      <ol
        className="order-1 flex list-none items-center justify-center gap-1.5 p-0 sm:order-2"
        aria-label={labels.stepsLabel}
      >
        {Array.from({ length: stepsCount }).map((_, i) => (
          <li key={i} className="flex">
            <button
              type="button"
              aria-current={i === activeIndex ? "step" : undefined}
              onClick={() => onJump(i)}
              data-testid="tour-modal-step"
              className={cn(
                "size-2 rounded-full transition",
                i === activeIndex
                  ? "w-6 bg-primary"
                  : "bg-surface-border hover:bg-text-muted",
              )}
              aria-label={labels.stepNumbers[i]}
            />
          </li>
        ))}
      </ol>

      <div className="order-2 flex items-center gap-2 sm:order-3">
        {isFirst ? (
          <Button
            onClick={onNext}
            data-testid="tour-modal-next"
            color={primaryButtonColor}
            radius="md"
            rightSection={<Icon name="chevronRight" size={16} />}
            className="flex-1! sm:flex-none!"
          >
            {labels.getStarted}
          </Button>
        ) : (
          <>
            <Button
              variant="filled"
              color="dark"
              radius="md"
              onClick={onBack}
              data-testid="tour-modal-back"
              className="flex-1! sm:flex-none!"
            >
              {labels.back}
            </Button>
            <Button
              color={primaryButtonColor}
              radius="md"
              onClick={onNext}
              data-testid="tour-modal-next"
              rightSection={
                isLast ? (
                  <Icon name="check" size={16} />
                ) : (
                  <Icon name="chevronRight" size={16} />
                )
              }
              className="flex-1! sm:flex-none!"
            >
              {isLast ? labels.finish : labels.next}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
